import { useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Pictogram } from '@cbhq/cds-web/illustrations';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Pressable } from '@cbhq/cds-web/system';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { Erc20Token } from ':cloud/types/ts_types';
import { PAYMASTER_ERC20_CONTACT_US_LINK } from ':cloud/utils/links';
import { onExternalLinkPress } from ':cloud/utils/onExternalLinkPress';
import { openUrlInNewWindow } from ':cloud/utils/openUrlInNewWindow';

import { BaseNetworkDropdown } from './BaseNetworkDropdown';

function getShortenedAddress(address: string) {
  // Slice the first 6 characters and the last 4 characters, then concatenate them with "..."
  const shortened = `${address.slice(0, 6)}...${address.slice(-4)}`;
  return shortened;
}

type Erc20PaymasterConfigProps = {
  acceptedTokens?: Erc20Token[];
  setSelectedNetwork: (network: string) => void;
  selectedNetwork: string;
};

function Erc20EmptyStateConfig() {
  return (
    <VStack alignItems="center" width="100%" gap={2} spacingVertical={4}>
      <Pictogram name="settings" />
      <TextHeadline as="p">Contact us to add allowed token(s)</TextHeadline>
      <Button variant="primary" onPress={onExternalLinkPress(PAYMASTER_ERC20_CONTACT_US_LINK)}>
        Contact us
      </Button>
    </VStack>
  );
}

export function Erc20PaymasterConfig({
  acceptedTokens,
  setSelectedNetwork,
  selectedNetwork,
}: Erc20PaymasterConfigProps) {
  const blockExplorer = useMemo(() => {
    if (selectedNetwork === 'Base Mainnet') {
      return 'https://basescan.org';
    }
    return 'https://sepolia.basescan.org';
  }, [selectedNetwork]);

  const networkTokens = useMemo(() => {
    return acceptedTokens?.filter((token) => {
      if (selectedNetwork === 'Base Mainnet') {
        return token?.network === 'base';
      }
      return token?.network === 'base-sepolia';
    });
  }, [acceptedTokens, selectedNetwork]);

  const tokenRows = useMemo(() => {
    return networkTokens?.map((token) => (
      <TableRow disableHoverIndicator>
        <TableCell>
          <TextHeadline as="h3">{token?.name}</TextHeadline>
        </TableCell>
        <TableCell>
          <Pressable
            background="transparent"
            onPress={() => openUrlInNewWindow(`${blockExplorer}/address/${token?.address}`)}
          >
            <HStack alignItems="center" gap={1}>
              <TextHeadline as="h3">{getShortenedAddress(token?.address)}</TextHeadline>
              <Icon name="diagonalUpArrow" size="s" color="foreground" />
            </HStack>
          </Pressable>
        </TableCell>
        <TableCell>
          <Pressable
            background="transparent"
            onPress={() => openUrlInNewWindow(`${blockExplorer}/address/${token?.receiver}`)}
          >
            <HStack alignItems="center" gap={1}>
              <TextHeadline as="h3">{getShortenedAddress(token?.receiver)}</TextHeadline>
              <Icon name="diagonalUpArrow" size="s" color="foreground" />
            </HStack>
          </Pressable>
        </TableCell>
      </TableRow>
    ));
  }, [blockExplorer, networkTokens]);

  return (
    <VStack gap={5}>
      <HStack gap={2} alignItems="center" justifyContent="space-between">
        <VStack>
          <TextTitle3 as="h1">ERC-20 Paymaster</TextTitle3>
          <TextBody as="p" color="foregroundMuted">
            Provide users with options to pay for gas in ERC-20 tokens.
          </TextBody>
        </VStack>
        <BaseNetworkDropdown
          setSelectedNetwork={setSelectedNetwork}
          selectedNetwork={selectedNetwork}
        />
      </HStack>
      <VStack borderRadius="rounded" bordered spacingHorizontal={2} spacingVertical={3} gap={4}>
        <HStack alignItems="center" gap={2} spacingHorizontal={2}>
          <Box
            dangerouslySetBackground="var(--foreground)"
            borderRadius="roundedFull"
            alignItems="center"
            justifyContent="center"
            display="flex"
            width={24}
            minWidth={24}
            height={24}
          >
            <Icon name="currencies" size="s" color="secondary" />
          </Box>
          <VStack>
            <TextHeadline as="p">Allowed token(s)</TextHeadline>
            <TextBody as="p" color="foregroundMuted">
              Token(s) your users can use to pay for gas.
            </TextBody>
          </VStack>
        </HStack>

        <Box>
          {tokenRows?.length ? (
            <Table maxHeight={360}>
              <TableHeader>
                <TableRow>
                  <TableCell title="Token symbol" />
                  <TableCell title="Contract address" />
                  <TableCell title="Receiving address" />
                </TableRow>
              </TableHeader>
              <TableBody>{tokenRows}</TableBody>
            </Table>
          ) : (
            <Erc20EmptyStateConfig />
          )}
        </Box>
      </VStack>
    </VStack>
  );
}
